import { useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'

import { getModuleVersionInstances, isArtifactFeedServiceConfigured } from '@modules/artifactfeeds'
import type { HostedModule } from '@modules/gateways'
import { selectGatewayById } from '@modules/gateways/stores/gateways.selector'
import { useGatewaysStore } from '@modules/gateways/stores/gateways.store'
import { selectCurrentProjectId } from '@modules/projects/stores/projects.selector'
import { useProjectsStore } from '@modules/projects/stores/projects.store'

import type { UseModulesParams } from './interfaces'
import { buildGatewayVirtualModules } from './mappers'
import { collectModuleVersionInstanceIds, resolveGateway } from './utils'

const EMPTY_MODULES: HostedModule[] = []
const EMPTY_IDS: string[] = []

export function useModules({ gatewayId, gateway: gatewayOverride }: UseModulesParams) {
  const currentProjectId = useProjectsStore(selectCurrentProjectId)
  const storeGateway = useGatewaysStore(selectGatewayById(gatewayId))
  const gateway = resolveGateway(gatewayOverride, storeGateway)
  const hostedModules = gateway?.modules ?? EMPTY_MODULES
  const fallbackIds = gateway?.moduleVersionInstanceIds ?? EMPTY_IDS

  const moduleVersionInstanceIds = useMemo(
    () => collectModuleVersionInstanceIds(hostedModules, fallbackIds),
    [fallbackIds, hostedModules]
  )

  const canFetchFromArtifactFeed =
    Boolean(currentProjectId) && moduleVersionInstanceIds.length > 0 && isArtifactFeedServiceConfigured()

  const resolvedGatewayId = gateway?.gatewayId ?? gatewayId ?? ''

  const { data: moduleVersionInstances = [] } = useQuery({
    queryKey: ['gateway-modules', resolvedGatewayId, currentProjectId, moduleVersionInstanceIds],
    queryFn: () => {
      if (!currentProjectId) {
        return Promise.resolve([])
      }

      return getModuleVersionInstances(currentProjectId, moduleVersionInstanceIds)
    },
    enabled: canFetchFromArtifactFeed,
  })

  const data = useMemo(
    () =>
      buildGatewayVirtualModules({
        hostedModules,
        moduleVersionInstances,
      }),
    [hostedModules, moduleVersionInstances]
  )

  return { data }
}
